import { IBuilderModel } from '../models/workflow-builder.model';
import { buildWorkflowVisualGraph } from './workflow-visual-graph';

export type ExecutionLayoutPositions = Record<string, { x: number; y: number }>;

interface ComponentGraph {
  members: string[][];
  componentOf: Map<string, number>;
  outgoing: Array<Set<number>>;
  incoming: Array<Set<number>>;
}

const stronglyConnectedComponents = (
  nodes: string[],
  adjacency: Map<string, Set<string>>
): ComponentGraph => {
  let index = 0;
  const indexes = new Map<string, number>();
  const lowLinks = new Map<string, number>();
  const stack: string[] = [];
  const onStack = new Set<string>();
  const members: string[][] = [];

  const visit = (node: string): void => {
    indexes.set(node, index);
    lowLinks.set(node, index++);
    stack.push(node);
    onStack.add(node);

    (adjacency.get(node) ?? new Set()).forEach(target => {
      if (!indexes.has(target)) {
        visit(target);
        lowLinks.set(
          node,
          Math.min(lowLinks.get(node) as number, lowLinks.get(target) as number)
        );
      } else if (onStack.has(target)) {
        lowLinks.set(
          node,
          Math.min(lowLinks.get(node) as number, indexes.get(target) as number)
        );
      }
    });

    if (lowLinks.get(node) !== indexes.get(node)) return;
    const component: string[] = [];
    let current = '';
    do {
      current = stack.pop() as string;
      onStack.delete(current);
      component.push(current);
    } while (current !== node);
    members.push(component);
  };

  nodes.forEach(node => {
    if (!indexes.has(node)) visit(node);
  });

  const componentOf = new Map<string, number>();
  members.forEach((component, componentIndex) =>
    component.forEach(node => componentOf.set(node, componentIndex))
  );
  const outgoing = members.map(() => new Set<number>());
  const incoming = members.map(() => new Set<number>());
  adjacency.forEach((targets, source) => {
    const sourceComponent = componentOf.get(source) as number;
    targets.forEach(target => {
      const targetComponent = componentOf.get(target) as number;
      if (sourceComponent === targetComponent) return;
      outgoing[sourceComponent].add(targetComponent);
      incoming[targetComponent].add(sourceComponent);
    });
  });
  return { members, componentOf, outgoing, incoming };
};

/**
 * A topology-first vertical layout. It identifies a convergence spine, places
 * detours that rejoin the next spine component in the floor between them, and
 * keeps terminal outcomes beside their exact source path.
 */
export function executionAwareVerticalLayout(
  model: IBuilderModel,
  sizeFor: (nodeKey: string) => { width: number; height: number }
): ExecutionLayoutPositions {
  const graph = buildWorkflowVisualGraph(model);
  const outgoingEdges = new Map<string, typeof graph.edges>();
  graph.edges.forEach(edge => {
    const list = outgoingEdges.get(edge.sourceKey) ?? [];
    list.push(edge);
    outgoingEdges.set(edge.sourceKey, list);
  });
  const structural = graph.nodes.filter(node =>
    graph.edges.some(edge => edge.sourceKey === node.key)
  );
  const structuralKeys = new Set(structural.map(node => node.key));
  const adjacency = new Map<string, Set<string>>();
  structural.forEach(node => adjacency.set(node.key, new Set()));
  graph.edges.forEach(edge => {
    if (
      structuralKeys.has(edge.sourceKey) &&
      structuralKeys.has(edge.targetKey)
    ) {
      adjacency.get(edge.sourceKey)?.add(edge.targetKey);
    }
  });

  if (!structural.length) {
    return Object.fromEntries(
      graph.nodes.map((node, index) => [node.key, { x: index * 420, y: 0 }])
    );
  }

  const components = stronglyConnectedComponents(
    structural.map(node => node.key),
    adjacency
  );
  const reachableMemo = new Map<string, boolean>();
  const canReach = (from: number, target: number): boolean => {
    if (from === target) return true;
    const memoKey = `${from}:${target}`;
    if (reachableMemo.has(memoKey))
      return reachableMemo.get(memoKey) as boolean;
    const seen = new Set<number>([from]);
    const queue = [...components.outgoing[from]];
    while (queue.length) {
      const current = queue.shift() as number;
      if (current === target) {
        reachableMemo.set(memoKey, true);
        return true;
      }
      if (seen.has(current)) continue;
      seen.add(current);
      queue.push(...components.outgoing[current]);
    }
    reachableMemo.set(memoKey, false);
    return false;
  };
  const reachCount = (from: number): number =>
    components.members.reduce(
      (count, _members, target) => count + (canReach(from, target) ? 1 : 0),
      0
    );

  const roots = components.members
    .map((_members, index) => index)
    .filter(index => components.incoming[index].size === 0);
  const primaryRoot = [...roots].sort(
    (a, b) => reachCount(b) - reachCount(a) || a - b
  )[0];

  const componentHasTerminalOutcome = (component: number): boolean =>
    components.members[component].some(key =>
      (outgoingEdges.get(key) ?? []).some(
        edge => !structuralKeys.has(edge.targetKey)
      )
    );
  const trunk = [primaryRoot];
  const trunkSet = new Set(trunk);
  let current = primaryRoot;
  // The trunk grows by at most one component per step, so it can never
  // contain more than every component; that count bounds the walk.
  for (let step = 0; step < components.members.length; step++) {
    const candidates = [...components.outgoing[current]].filter(
      candidate => !trunkSet.has(candidate)
    );
    if (!candidates.length) break;
    const scored = candidates
      .map(candidate => ({
        candidate,
        rejoin: candidates.filter(
          other => other !== candidate && canReach(other, candidate)
        ).length,
        future: reachCount(candidate),
        incoming: components.incoming[candidate].size,
      }))
      .sort(
        (a, b) =>
          b.rejoin - a.rejoin ||
          b.incoming - a.incoming ||
          b.future - a.future ||
          a.candidate - b.candidate
      );
    const next = scored[0];
    // A last-floor side branch that only leads to a terminal outcome is a
    // detour, not a continuation of the central execution spine.
    if (
      componentHasTerminalOutcome(current) &&
      next.rejoin === 0 &&
      components.outgoing[next.candidate].size === 0
    )
      break;
    current = next.candidate;
    trunk.push(current);
    trunkSet.add(current);
  }

  const X_GAP = 520;
  const Y_GAP = 560;
  const centres: Record<string, { x: number; y: number }> = {};
  const placedComponents = new Set<number>();
  const visualOrder = new Map(
    graph.nodes.map((node, index) => [node.key, index])
  );
  const placeComponent = (
    component: number,
    x: number,
    y: number,
    direction = 1
  ): void => {
    const members = [...components.members[component]].sort(
      (one, two) =>
        (visualOrder.get(one) as number) - (visualOrder.get(two) as number)
    );
    members.forEach((key, index) => {
      centres[key] = {
        x: x + direction * index * X_GAP * 0.82,
        y: y + index * Y_GAP * 0.28,
      };
    });
    placedComponents.add(component);
  };

  trunk.forEach((component, index) =>
    placeComponent(component, 0, index * Y_GAP)
  );
  const branchSideByTrunk = new Map<number, number>();
  let branchOrdinal = 0;
  for (let index = 0; index < trunk.length - 1; index++) {
    const source = trunk[index];
    const target = trunk[index + 1];
    const detours = components.members
      .map((_members, component) => component)
      .filter(
        component =>
          !trunkSet.has(component) &&
          !placedComponents.has(component) &&
          canReach(source, component) &&
          canReach(component, target)
      )
      .sort((a, b) => reachCount(b) - reachCount(a));
    if (!detours.length) continue;
    const side = branchOrdinal++ % 2 === 0 ? 1 : -1;
    branchSideByTrunk.set(source, side);
    detours.forEach((component, detourIndex) =>
      placeComponent(
        component,
        side * X_GAP * (1 + detourIndex * 0.9),
        index * Y_GAP + Y_GAP * (0.3 + detourIndex * 0.22),
        side
      )
    );
  }

  // Side work after the last central floor (for example a final resubmission
  // path) still belongs to that floor, even though there is no next spine node.
  const lastTrunk = trunk[trunk.length - 1];
  const tailComponents = [...components.outgoing[lastTrunk]].filter(
    component => !placedComponents.has(component)
  );
  if (tailComponents.length) {
    const side = branchOrdinal++ % 2 === 0 ? 1 : -1;
    branchSideByTrunk.set(lastTrunk, side);
    tailComponents.forEach((component, index) =>
      placeComponent(
        component,
        side * X_GAP * (1 + index * 0.9),
        (trunk.length - 1) * Y_GAP + Y_GAP * (0.35 + index * 0.2),
        side
      )
    );
  }

  // Disconnected roots and unusual branches use free outer lanes. This is the
  // generic fallback for topology that is neither spine nor rejoining detour.
  let outerLane = 3;
  components.members.forEach((_members, component) => {
    if (placedComponents.has(component)) return;
    const parent = [...components.incoming[component]].find(candidate =>
      placedComponents.has(candidate)
    );
    const parentMember = parent == null ? null : components.members[parent][0];
    const parentCentre = parentMember ? centres[parentMember] : null;
    const side = outerLane % 2 ? -1 : 1;
    placeComponent(
      component,
      parentCentre?.x ?? side * X_GAP * outerLane,
      parentCentre ? parentCentre.y + Y_GAP * 0.45 : 0,
      side
    );
    outerLane++;
  });

  const terminalNodes = graph.nodes.filter(
    node => !structuralKeys.has(node.key)
  );
  const trunkIndex = new Map(
    trunk.map((component, index) => [component, index])
  );
  const firstTrunkFloorByTerminalCode = new Map<string, number>();
  terminalNodes.forEach(node => {
    const incoming = graph.edges.find(edge => edge.targetKey === node.key);
    const sourceComponent = incoming
      ? components.componentOf.get(incoming.sourceKey)
      : undefined;
    const floor =
      sourceComponent == null ? undefined : trunkIndex.get(sourceComponent);
    if (floor == null) return;
    const previous = firstTrunkFloorByTerminalCode.get(node.stateCode);
    firstTrunkFloorByTerminalCode.set(
      node.stateCode,
      previous == null ? floor : Math.min(previous, floor)
    );
  });
  const finalStateCode = [...firstTrunkFloorByTerminalCode.entries()].sort(
    (one, two) => two[1] - one[1]
  )[0]?.[0];
  const terminalUse = new Map<string, number>();
  terminalNodes.forEach(node => {
    const incoming = graph.edges.find(edge => edge.targetKey === node.key);
    if (!incoming) return;
    const source = centres[incoming.sourceKey];
    if (!source) return;
    const sourceComponent = components.componentOf.get(incoming.sourceKey);
    const sourceEdges = outgoingEdges.get(incoming.sourceKey) ?? [];
    const structuralTargets = sourceEdges.filter(edge =>
      structuralKeys.has(edge.targetKey)
    );
    const soleTerminalPath =
      structuralTargets.length === 0 && sourceEdges.length === 1;
    const sourceBelongsToLastFloor =
      sourceComponent != null && canReach(lastTrunk, sourceComponent);
    const placeBelow =
      soleTerminalPath ||
      (node.stateCode === finalStateCode && sourceBelongsToLastFloor);
    const useKey = `${incoming.sourceKey}:${placeBelow ? 'down' : 'side'}`;
    const use = terminalUse.get(useKey) ?? 0;
    terminalUse.set(useKey, use + 1);

    if (placeBelow) {
      centres[node.key] = {
        x: source.x + use * X_GAP * 0.45,
        y: source.y + Y_GAP * (0.72 + use * 0.28),
      };
      return;
    }
    const sourceSide = Math.sign(source.x);
    const branchSide =
      sourceComponent == null ? 0 : branchSideByTrunk.get(sourceComponent) ?? 0;
    const side = sourceSide || -branchSide || (terminalUse.size % 2 ? -1 : 1);
    centres[node.key] = {
      x: source.x + side * X_GAP * (1 + use * 0.65),
      y: source.y + use * Y_GAP * 0.18,
    };
  });

  // Any isolated visual node omitted by the structural pass gets a deterministic lane.
  graph.nodes.forEach((node, index) => {
    if (!centres[node.key]) centres[node.key] = { x: index * X_GAP, y: 0 };
  });

  const positions: ExecutionLayoutPositions = {};
  graph.nodes.forEach(node => {
    const size = sizeFor(node.key);
    const centre = centres[node.key];
    positions[node.key] = {
      x: Math.round(centre.x - size.width / 2),
      y: Math.round(centre.y - size.height / 2),
    };
  });
  return positions;
}

/** Rotate the execution-aware floor model so progression runs left-to-right. */
export function executionAwareHorizontalLayout(
  model: IBuilderModel,
  sizeFor: (nodeKey: string) => { width: number; height: number }
): ExecutionLayoutPositions {
  const vertical = executionAwareVerticalLayout(model, nodeKey => {
    const size = sizeFor(nodeKey);
    return { width: size.height, height: size.width };
  });

  return Object.fromEntries(
    Object.entries(vertical).map(([nodeKey, position]) => [
      nodeKey,
      { x: position.y, y: position.x },
    ])
  );
}
